import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { RiCloseLine, RiUploadCloud2Line } from 'react-icons/ri';
import axios from 'axios';
import './MedicineModal.css';
import JoditEditor from 'jodit-react';

const MedicineModal = ({ medicine, isEditing, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: medicine?.name || '',
        category: medicine?.category || '',
        description: medicine?.description || '',
        packOf: medicine?.packOf || '',
        batchNumber: medicine?.batchNumber || '',
        expiryDate: medicine?.expiryDate ? medicine.expiryDate.split('T')[0] : '',
        mrp: medicine?.mrp || '',
        salePrice: medicine?.salePrice || '',
        stockQuantity: medicine?.stockQuantity || '',
        dealRate: medicine?.dealRate || '',
        brand: medicine?.brand || '',
        gst: medicine?.gst || '',
        salt: medicine?.salt || '',
        department: medicine?.department 
            ? medicine.department.flatMap(dept => 
                dept.split(',').map(d => d.trim()).filter(d => d !== '')
              )
            : [],
    });

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const editor = useRef(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get('https://api.swasthmitra.com/api/category/get');
                setCategories(response.data.categories);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };

        const fetchDepartments = async () => {
            try {
                const response = await axios.get('https://api.swasthmitra.com/api/category/selected');
                setDepartments(response.data.categories);
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };

        fetchCategories();
        fetchDepartments();
    }, []);

    const removeImage = (index) => {
        setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDepartmentChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => {
            // Remove duplicates and empty values
            let newDepartments = checked
                ? [...new Set([...prev.department, value])]
                : prev.department.filter(dept => dept !== value);
            
            return {
                ...prev,
                department: newDepartments
            };
        });
    };

    const handleImageChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setImages((prevImages) => [...prevImages, ...selectedFiles]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const submitData = new FormData();

        // Append form data fields
        Object.keys(formData).forEach(key => {
            if (key === 'department') {
                // Remove duplicates and empty values
                const uniqueDepts = [...new Set(formData[key].filter(dept => dept.trim() !== ''))];
                uniqueDepts.forEach(dept => submitData.append('department[]', dept));
            } else {
                submitData.append(key, formData[key]);
            }
        });

        // Append multiple images
        if (images.length > 0) {
            images.forEach((img) => {
                submitData.append('images', img);
            });
        }

        try {
            if (isEditing) {
                await axios.patch(
                    `https://api.swasthmitra.com/api/medicine/${medicine._id}`,
                    submitData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            } else {
                await axios.post(
                    'https://api.swasthmitra.com/api/medicine/add',
                    submitData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            }
            onSave();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Error saving medicine');
        } finally {
            setLoading(false);
        }
    };

    const isDepartmentSelected = (deptValue) => {
        return formData.department.includes(deptValue);
    };

    return (
        <motion.div
            className="medicine-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div
                className="medicine-modal-content"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
            >
                <button className="medicine-modal-close-btn" onClick={onClose}>
                    <RiCloseLine />
                </button>

                <div className="medicine-modal-header">
                    <h2>{isEditing ? 'Edit Medicine' : 'Add Medicine'}</h2>
                </div>

                {error && <div className="medicine-modal-error">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="medicine-form-grid">
                        <div className="medicine-form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Departments</label>
                            <div className="medicine-checkbox-container">
                                {departments.map(dept => {
                                    const deptValue = dept.slug || dept.name;
                                    return (
                                        <div key={dept._id} className="medicine-checkbox-item">
                                            <input
                                                type="checkbox"
                                                id={`dept-${dept._id}`}
                                                value={deptValue}
                                                checked={isDepartmentSelected(deptValue)}
                                                onChange={handleDepartmentChange}
                                                className="medicine-checkbox-input"
                                            />
                                            <label htmlFor={`dept-${dept._id}`} className="medicine-checkbox-label">
                                                {dept.name}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="medicine-form-group">
                            <label>Category</label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category.name} value={category.name}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="medicine-form-group">
                            <label>Brand</label>
                            <input
                                type="text"
                                name="brand"
                                value={formData.brand}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Pack Size</label>
                            <input
                                type="text"
                                name="packOf"
                                value={formData.packOf}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Batch Number</label>
                            <input
                                type="text"
                                name="batchNumber"
                                value={formData.batchNumber}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Expiry Date</label>
                            <input
                                type="date"
                                name="expiryDate"
                                value={formData.expiryDate}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>MRP</label>
                            <input
                                type="number"
                                name="mrp"
                                value={formData.mrp}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Sale Price</label>
                            <input
                                type="number"
                                name="salePrice"
                                value={formData.salePrice}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Stock Quantity</label>
                            <input
                                type="number"
                                name="stockQuantity"
                                value={formData.stockQuantity}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Deal Rate</label>
                            <input
                                type="number"
                                name="dealRate"
                                value={formData.dealRate}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>GST (%)</label>
                            <input
                                type="number"
                                name="gst"
                                value={formData.gst}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="medicine-form-group">
                            <label>Salt</label>
                            <input
                                type="text"
                                name="salt"
                                value={formData.salt}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="medicine-form-group full-width">
                        <label>Description</label>
                        <JoditEditor
                            ref={editor}
                            value={formData.description}
                            onChange={(newContent) => {
                                setFormData(prev => ({
                                    ...prev,
                                    description: newContent
                                }));
                            }}
                        />
                    </div>

                    <div className="medicine-form-group">
                        <label className="medicine-upload-label">
                            <RiUploadCloud2Line />
                            {images.length > 0 ? `${images.length} images selected` : 'Upload Images'}
                            <input
                                type="file"
                                onChange={handleImageChange}
                                accept="image/*"
                                multiple
                                style={{ display: "none" }}
                            />
                        </label>

                        <div className="image-preview1">
                            {images.map((image, index) => (
                                <div key={index} className="image-container1">
                                    <img
                                        src={URL.createObjectURL(image)}
                                        alt={`preview-${index}`}
                                        className="image-preview-thumbnail"
                                    />
                                    <button className="delete-button4" onClick={() => removeImage(index)}>❌</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="medicine-modal-actions">
                        <button type="button" className="medicine-modal-cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="medicine-modal-save-btn" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Medicine'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

export default MedicineModal;